import theme from "@shared/utils/themeColors";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { v4 as uuidv4 } from "uuid";
import { useMediaUpload } from "../hooks/useMediaUpload";
import { MediaFile, MediaType } from "../types/media.types";

export interface ImageFile {
  file: File;
  id: string;
  previewUrl?: string;
  uploadedUrl?: string;
  progress?: number;
  error?: string;
}

export interface ImageUploaderProps {
  onImageSelect: (image: ImageFile) => void;
  acceptedTypes?: string;
  maxSizeMB?: number;
  className?: string;
  dropzoneText?: {
    title?: string;
    subtitle?: string;
    sizeInfo?: string;
  };
  uploadMedia?: (file: MediaFile) => Promise<string>;
}

export const ImageUploader = ({
  onImageSelect,
  acceptedTypes = "image/*",
  maxSizeMB = 10,
  className = "",
  dropzoneText = {
    title: "Upload an image",
    subtitle: "or drag and drop",
    sizeInfo: "Images up to 10MB",
  },
  uploadMedia: customUploadMedia,
}: ImageUploaderProps) => {
  const [error, setError] = useState<string | undefined>();
  const { uploadMedia: defaultUploadMedia } = useMediaUpload();
  const uploadMedia = customUploadMedia || defaultUploadMedia;
  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  const onDrop = async (acceptedFiles: File[]) => {
    // Only process the first file for single image upload
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];

    // Validate file size
    if (file.size > maxSizeBytes) {
      setError(`File size exceeds ${maxSizeMB}MB limit`);
      return;
    }

    // Create preview URL
    const preview = URL.createObjectURL(file);

    // Create media file object
    const mediaFile: MediaFile = {
      file,
      preview,
      type: MediaType.IMAGE,
      progress: 0,
    };

    // Create image file object
    const imageFile: ImageFile = {
      file,
      id: uuidv4(),
      previewUrl: preview,
      progress: 0,
    };

    try {
      console.log("Starting image upload...");

      // First notify with the preview
      onImageSelect(imageFile);

      // Then upload the file
      const uploadedUrl = await uploadMedia(mediaFile);
      console.log("Image uploaded successfully:", uploadedUrl);

      // Update with the uploaded URL
      const updatedImageFile: ImageFile = {
        ...imageFile,
        uploadedUrl,
        progress: 100,
      };

      onImageSelect(updatedImageFile);
    } catch (err) {
      console.error("Error uploading image:", err);
      setError(err instanceof Error ? err.message : "Upload failed");

      // Update the image file with error
      const errorImageFile: ImageFile = {
        ...imageFile,
        error: err instanceof Error ? err.message : "Upload failed",
      };

      onImageSelect(errorImageFile);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpg", ".jpeg", ".png", ".gif", ".webp"],
    },
    maxFiles: 1,
    multiple: false,
  });

  return (
    <div className={`${className}`}>
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors
          ${
            isDragActive
              ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20"
              : "border-gray-300 dark:border-gray-600 hover:border-indigo-400 dark:hover:border-indigo-500"
          }`}
      >
        <input {...getInputProps()} />

        <div className="py-6">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48"
            aria-hidden="true"
          >
            <path
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <div className="flex text-sm text-gray-600 justify-center">
            <span className="relative cursor-pointer rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none">
              {dropzoneText.title}
            </span>
            <p className="pl-1">{dropzoneText.subtitle}</p>
          </div>
          <p className={`text-xs ${theme.text.muted}`}>
            {dropzoneText.sizeInfo}
          </p>
        </div>
      </div>

      {error && (
        <div className={`mt-2 text-sm ${theme.text.error}`}>{error}</div>
      )}
    </div>
  );
};
